import { useCallback, useEffect, useMemo, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase, type Memory } from "@/lib/supabase";
import { queryClient } from "@/lib/queryClient";
import { useAuth } from "./useAuth";
import { useActiveSpace } from "./useActiveSpace";

export interface MemoryCollectionRef {
  collection_id: string;
  collection: { id: string; name: string; icon: string; color: string } | null;
}

export interface MemoryWithChildren extends Memory {
  memory_participants: Array<{
    id: string;
    child_id: string | null;
    space_member_id: string | null;
    display_name: string | null;
    children: { id: string; name: string; avatar_url: string | null; avatar_color: string | null } | null;
    space_members: { id: string; display_name: string | null; user_id: string | null } | null;
  }>;
  collection_memories: MemoryCollectionRef[];
  /** Whether the *current* user has favorited this memory. Derived from the
   *  embedded memory_favorites array (RLS limits it to the user's own rows). */
  is_favorited: boolean;
  /** Total favorites across the space — denormalized counter on memories.
   *  Not shown in the UI; used for AI weighting + internal product curation. */
  favorite_count: number;
}

export type RecordedByFilter = "anyone" | "me" | "contributors";
export type SortOrder = "newest" | "oldest" | "random";

export interface MemoryFilters {
  searchTerm?: string;
  categories?: string[];
  childIds?: string[];
  recordedBy?: RecordedByFilter;
  dateFrom?: Date | null;
  dateTo?: Date | null;
  sortOrder?: SortOrder;
  favoritesOnly?: boolean;
}

// memory_favorites is embedded with !left so memories without any favorite
// row still come through. RLS on memory_favorites limits the embedded array
// to rows where user_id = auth.uid(), so length > 0 ⇔ "favorited by me".
const FULL_SELECT =
  "*, memory_participants(id,child_id,space_member_id,display_name,children:children(id,name,avatar_url,avatar_color),space_members:space_members(id,display_name,user_id)), collection_memories(collection_id, collection:collections(id, name, icon, color)), memory_favorites!left(user_id)";

/** Invalidate all memories queries. Call after any mutation that creates/updates/deletes memories. */
export function invalidateMemories() {
  return queryClient.invalidateQueries({ queryKey: ["memories"] });
}

async function fetchMemoriesForSpace(spaceId: string): Promise<MemoryWithChildren[]> {
  // Filter by active space so Members of multiple spaces never see a mixed
  // feed. RLS would otherwise return the union of every accessible space.
  //   • capsule_state='default' excludes Time Capsules (separate query below)
  //   • approval_status='approved' excludes child-pending memories
  const { data, error } = await supabase
    .from("memories")
    .select(FULL_SELECT)
    .eq("space_id", spaceId)
    .eq("capsule_state", "default")
    .eq("approval_status", "approved")
    .neq("status", "pending")
    .order("memory_date", { ascending: false });
  if (error) throw error;
  return ((data as unknown as Array<MemoryWithChildren & { memory_favorites?: Array<{ user_id: string }> }>) ?? [])
    .map((m) => ({
      ...m,
      is_favorited: (m.memory_favorites?.length ?? 0) > 0,
      favorite_count: m.favorite_count ?? 0,
    }));
}

/**
 * Fetches all memories for the active space and applies client-side filters.
 * Pass `filters` wrapped in `useMemo` to avoid unnecessary recomputation.
 *
 * Stays fresh via two mechanisms:
 *   1. TanStack Query cache + persistence (cold open shows last-known data).
 *   2. Supabase Realtime channel on `memories` + `memory_participants` —
 *      any change invalidates the query so the next render refetches.
 *
 * Mutation sites must call `invalidateMemories()` (or `queryClient.invalidateQueries
 * ({ queryKey: ['memories'] })`) after success.
 */
export function useMemories(childId?: string | null, filters?: MemoryFilters) {
  const { user } = useAuth();
  const { space: activeSpace } = useActiveSpace();
  const qc = useQueryClient();
  const userId = user?.id;
  const spaceId = activeSpace?.id ?? null;
  // Unique per hook instance so multiple consumers don't collide on the same
  // Supabase channel name (which would throw "cannot add callbacks after subscribe").
  const instanceId = useRef(crypto.randomUUID()).current;

  const query = useQuery({
    queryKey: ["memories", userId, spaceId],
    enabled: !!userId && !!spaceId,
    queryFn: () => fetchMemoriesForSpace(spaceId!),
  });

  const rawMemories = query.data;

  // ── Realtime subscription ────────────────────────────────────────────────
  // Separate effect so the channel lifecycle (subscribe / unsubscribe) is
  // decoupled from the query lifecycle. Switching space tears the old
  // channel down and opens a new one filtered by the new space_id.
  useEffect(() => {
    if (!userId || !spaceId) return;

    const channel = supabase
      .channel(`memories-changes:${userId}:${spaceId}:${instanceId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "memories",
          filter: `space_id=eq.${spaceId}`,
        },
        () => {
          void qc.invalidateQueries({ queryKey: ["memories", userId, spaceId] });
        },
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "memory_favorites",
          filter: `user_id=eq.${userId}`,
        },
        () => {
          void qc.invalidateQueries({ queryKey: ["memories", userId, spaceId] });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [qc, userId, spaceId, instanceId]);

  // ── Client-side filtering ─────────────────────────────────────────────────
  // Applied to the cached query data — changing filters does NOT trigger a
  // refetch, just a recomputation.
  const memories = useMemo(() => {
    let list = [...(rawMemories ?? [])];

    // childId (legacy param — kept for backward compat)
    if (childId) {
      list = list.filter((m) =>
        m.memory_participants.some((mp) => mp.child_id === childId),
      );
    }

    // childIds filter
    if (filters?.childIds?.length) {
      list = list.filter((m) =>
        m.memory_participants.some(
          (mp) => mp.child_id !== null && filters.childIds!.includes(mp.child_id),
        ),
      );
    }

    // searchTerm — matches ai_chapter and raw_transcript
    if (filters?.searchTerm?.trim()) {
      const term = filters.searchTerm.toLowerCase();
      list = list.filter(
        (m) =>
          m.ai_chapter?.toLowerCase().includes(term) ||
          m.raw_transcript?.toLowerCase().includes(term),
      );
    }

    // categories
    if (filters?.categories?.length) {
      list = list.filter((m) =>
        m.categories.some((c) => filters.categories!.includes(c)),
      );
    }

    // recordedBy
    if (filters?.recordedBy === "me") {
      // "me" means recorded by the current user specifically, not by a
      // contributor or a co-parent. Matches by user_id so this works
      // correctly for both owners and co-parents.
      list = list.filter((m) => m.user_id === user?.id);
    } else if (filters?.recordedBy === "contributors") {
      list = list.filter((m) => !!m.contributor_id || !!m.contributor_name);
    }

    // favoritesOnly — narrow to memories the current user has hearted
    if (filters?.favoritesOnly) {
      list = list.filter((m) => m.is_favorited);
    }

    // dateFrom / dateTo — filter against the user-editable memory_date
    // (falls back to created_at for legacy rows, though the migration
    // backfilled those so the fallback is defensive only).
    const dateOf = (m: MemoryWithChildren) => m.memory_date ?? m.created_at;
    if (filters?.dateFrom) {
      list = list.filter((m) => new Date(dateOf(m)) >= filters.dateFrom!);
    }
    if (filters?.dateTo) {
      const toEnd = new Date(filters.dateTo);
      toEnd.setHours(23, 59, 59, 999);
      list = list.filter((m) => new Date(dateOf(m)) <= toEnd);
    }

    // sortOrder
    if (filters?.sortOrder === "oldest") {
      list = list.slice().sort(
        (a, b) =>
          new Date(dateOf(a)).getTime() - new Date(dateOf(b)).getTime(),
      );
    } else if (filters?.sortOrder === "random") {
      list = list.slice().sort(() => Math.random() - 0.5);
    }
    // default: newest first — server already returns in this order

    return list;
  }, [rawMemories, childId, filters, user?.id]);

  // Optimistic local removal — used by Feed.tsx when a memory is deleted so
  // the row disappears immediately without waiting for the realtime echo.
  const removeMemory = useCallback(
    (id: string) => {
      qc.setQueryData<MemoryWithChildren[]>(
        ["memories", userId, spaceId],
        (prev) => prev?.filter((m) => m.id !== id),
      );
    },
    [qc, userId, spaceId],
  );

  const refresh = useCallback(
    () => qc.invalidateQueries({ queryKey: ["memories", userId, spaceId] }),
    [qc, userId, spaceId],
  );

  return {
    data: query.data,
    memories,
    isLoading: query.isLoading,
    isError: query.isError,
    error: (query.error as Error | null) ?? null,
    refetch: query.refetch,
    refresh,
    removeMemory,
  };
}

async function fetchTimeCapsulesForSpace(spaceId: string): Promise<MemoryWithChildren[]> {
  const { data, error } = await supabase
    .from("memories")
    .select(FULL_SELECT)
    .eq("space_id", spaceId)
    .eq("capsule_state", "time_capsule")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return ((data as unknown as Array<MemoryWithChildren & { memory_favorites?: Array<{ user_id: string }> }>) ?? [])
    .map((m) => ({
      ...m,
      is_favorited: (m.memory_favorites?.length ?? 0) > 0,
      favorite_count: m.favorite_count ?? 0,
    }));
}

/**
 * Fetches the user's Time Capsules (memories where capsule_state = 'time_capsule').
 * Same FULL_SELECT join as useMemories so cards have child names + collection refs.
 */
export function useTimeCapsules() {
  const { user } = useAuth();
  const { space: activeSpace } = useActiveSpace();
  const userId = user?.id;
  const spaceId = activeSpace?.id ?? null;

  const query = useQuery({
    queryKey: ["time-capsules", userId, spaceId],
    enabled: !!userId && !!spaceId,
    queryFn: () => fetchTimeCapsulesForSpace(spaceId!),
  });

  return {
    data: query.data,
    capsules: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: (query.error as Error | null) ?? null,
    refetch: query.refetch,
  };
}
